import React from 'react';

interface INote {
  panelist: string;
  notes: string;
  createdAt?: string;
  // Panelist could be an ID or an object with user info
  panelistInfo?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

interface INotes {
  data: INote[];
  panelistsMap?: Record<string, any>; // Map of panelist IDs to their info
}

export const Notes: React.FC<INotes> = ({ data, panelistsMap = {} }) => {
  if (!data || data.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center py-16 px-4'>
        <div className='relative mb-6'>
          <div className='w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center'>
            <svg
                className='w-12 h-12 text-blue-500'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
            >
              <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={1.5}
                  d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
              />
            </svg>
          </div>
          <div className='absolute -top-1 -right-1 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center'>
            <svg
                className='w-4 h-4 text-white'
                fill='currentColor'
                viewBox='0 0 20 20'
            >
              <path d='M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z' />
            </svg>
          </div>
        </div>
        <h3 className='text-lg font-semibold text-gray-900 mb-2'>
          No Panelist Notes Yet
        </h3>
        <p className='text-sm text-gray-500 text-center max-w-sm'>
          Panelist feedback and observations will appear here after they submit
          their notes
        </p>
      </div>
    );
  }

  return (
    <div className='space-y-4 p-6 w-full bg-gradient-to-br from-gray-50 to-white rounded-xl'>
      <div className='flex items-center justify-between mb-4'>
        <h3 className='text-sm font-bold text-gray-700 uppercase tracking-wider flex items-center gap-2'>
          <svg
              className='w-5 h-5 text-blue-500'
              fill='currentColor'
              viewBox='0 0 20 20'
          >
            <path d='M9 2a1 1 0 000 2h2a1 1 0 100-2H9z' />
            <path
                fillRule='evenodd'
                d='M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z'
                clipRule='evenodd'
            />
          </svg>
          Interview Notes ({ data.length })
        </h3>
      </div>

      <div className='space-y-4'>
        { data.map((note, index) => {
          // Try to get panelist info from the map or from the note itself
          const panelistId =
            typeof note.panelist === 'string' ? note.panelist : '';
          const panelistInfo = panelistsMap[panelistId] || note.panelistInfo;

          const panelistName = panelistInfo
            ? `${panelistInfo.firstName || ''} ${
              panelistInfo.lastName || ''
            }`.trim()
            : panelistId || `Panelist ${index + 1}`;

          const panelistInitials = panelistInfo
            ? `${panelistInfo.firstName?.[0] || ''}${
              panelistInfo.lastName?.[0] || ''
            }`.toUpperCase()
            : panelistName.substring(0, 2).toUpperCase();

          // Generate a color based on index for variety
          const colors = [
            'from-blue-500 to-blue-600',
            'from-purple-500 to-purple-600',
            'from-green-500 to-green-600',
            'from-orange-500 to-orange-600',
            'from-pink-500 to-pink-600',
            'from-teal-500 to-teal-600',
          ];
          const colorClass = colors[index % colors.length];

          return (
            <div
                key={index}
                className='group relative bg-white rounded-2xl shadow-sm border border-gray-200 hover:shadow-xl hover:border-blue-200 transition-all duration-300 overflow-hidden'
            >
              { /* Decorative accent */ }
              <div
                  className={`absolute top-0 left-0 w-1 h-full bg-gradient-to-b ${colorClass}`}
              />

              <div className='p-5 pl-6'>
                <div className='flex flex-col gap-4'>
                  { /* Panelist info header */ }
                  <div className='flex items-center gap-4'>
                    <div className='relative'>
                      <div
                          className={`w-12 h-12 rounded-full bg-gradient-to-br ${colorClass} text-white flex items-center justify-center text-base font-bold shadow-lg ring-2 ring-offset-2 ring-offset-white ring-blue-100 group-hover:ring-blue-200 transition-all duration-300`}
                      >
                        { panelistInitials }
                      </div>
                      { /* Online indicator */ }
                      <div className='absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white shadow-sm'></div>
                    </div>
                    <div className='flex flex-col flex-1'>
                      <div className='flex items-center gap-2'>
                        <span className='text-base font-bold text-gray-900'>
                          { panelistName }
                        </span>
                        <span className='px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full'>
                          Panelist
                        </span>
                      </div>
                      <div className='flex items-center gap-3 mt-1'>
                        { panelistInfo?.email && (
                          <span className='text-xs text-gray-500 flex items-center gap-1'>
                            <svg
                                className='w-3.5 h-3.5'
                                fill='currentColor'
                                viewBox='0 0 20 20'
                            >
                              <path d='M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z' />
                              <path d='M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z' />
                            </svg>
                            { panelistInfo.email }
                          </span>
                        ) }
                        { note.createdAt && (
                          <span className='text-xs text-gray-400 flex items-center gap-1'>
                            <svg
                                className='w-3.5 h-3.5'
                                fill='currentColor'
                                viewBox='0 0 20 20'
                            >
                              <path
                                  fillRule='evenodd'
                                  d='M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z'
                                  clipRule='evenodd'
                              />
                            </svg>
                            { new Date(note.createdAt).toLocaleDateString(
                              'en-US',
                              {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              },
                            ) }
                          </span>
                        ) }
                      </div>
                    </div>
                  </div>

                  { /* Note content */ }
                  <div className='relative'>
                    <div className='absolute -left-3 top-2 text-blue-200'>
                      <svg
                          className='w-8 h-8'
                          fill='currentColor'
                          viewBox='0 0 24 24'
                      >
                        <path d='M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z' />
                      </svg>
                    </div>
                    <div className='pl-8 pr-4'>
                      <p className='text-sm text-gray-700 leading-relaxed whitespace-pre-wrap'>
                        { note.notes }
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              { /* Bottom decorative gradient */ }
              <div
                  className={`h-1 bg-gradient-to-r ${colorClass} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
              />
            </div>
          );
        }) }
      </div>
    </div>
  );
};
